/**
 * TenantContext
 * Manages selected tenant state and provides tenant switching functionality
 * Integrates with Clerk Organizations for seamless org switching
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useOrganization } from "@clerk/react-router";
import { useTenants } from "@/hooks/useTenants";
import { setTenantGroup } from "@/lib/analytics";

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  memberRole: "owner" | "admin" | "member" | "viewer";
  tenantType: "company" | "investor";
  createdAt: string;
  updatedAt: string;
  clerkOrgId?: string; // Optional Clerk organization ID
}

interface TenantContextType {
  currentTenant: Tenant | null;
  tenants: Tenant[];
  dbUserId: string | undefined;
  isLoading: boolean;
  error: Error | null;
  selectTenant: (tenantId: string) => void;
}

export const TenantContext = createContext<TenantContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "stackverify_selected_tenant_id";

function persistTenant(tenantId: string) {
  localStorage.setItem(STORAGE_KEY, tenantId);
  try {
    setTenantGroup(tenantId);
  } catch (err) {
    console.error("Failed to set tenant group:", err);
  }
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { tenants, dbUserId, isLoading, error } = useTenants();
  const { organization: clerkOrg } = useOrganization();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [syncedClerkOrgId, setSyncedClerkOrgId] = useState<string | undefined>(
    undefined,
  );

  // Adjust state during render when inputs change (React-recommended pattern)
  // See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (tenants && tenants.length > 0) {
    const clerkOrgId = clerkOrg?.id;
    const clerkMatch = clerkOrgId
      ? tenants.find((t) => t.clerkOrgId === clerkOrgId)
      : undefined;

    if (!currentTenant) {
      // Initial load — unchanged behaviour: the saved selection (TenantSwitcher
      // selects without changing the Clerk org, e.g. an investor fund with no
      // Clerk org), else the first tenant. The active Clerk org is recorded as
      // already synced so it does not override that choice on the next render.
      const savedId = localStorage.getItem(STORAGE_KEY);
      const saved = savedId ? tenants.find((t) => t.id === savedId) : null;
      setCurrentTenant(saved ?? tenants[0]);
      if (clerkMatch) setSyncedClerkOrgId(clerkOrgId);
    } else if (clerkMatch && clerkOrgId !== syncedClerkOrgId) {
      // Active Clerk org changed and its tenant is loaded — follow it. The org is
      // recorded only once matched, so a just-created org whose tenant is not in
      // the list yet is synced when the re-fetched list arrives (sv-hzvy).
      setSyncedClerkOrgId(clerkOrgId);
      if (clerkMatch.id !== currentTenant.id) {
        setCurrentTenant(clerkMatch);
      }
    }
  }

  // Side effects (localStorage, analytics) run in effect, not during render
  useEffect(() => {
    if (currentTenant) {
      persistTenant(currentTenant.id);
    }
  }, [currentTenant]);

  const selectTenant = (tenantId: string) => {
    const tenant = tenants?.find((t) => t.id === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
    }
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        tenants: tenants || [],
        dbUserId,
        isLoading,
        error,
        selectTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}
